import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { startSessionTimer, clearSessionTimer } from '../services/sessionTimer';
import { apiClient } from '../services/apiClient';

type User = {
	id?: string;
	firstname?: string;
	lastname?: string;
	email?: string;
	image?: string | null;
	birthday?: string | null;
	name?: string;
	dni?: string;
	dni_type?: string;
};

type useUserStoreType = {
	user?: User;
	/** @deprecated CWE-922: el JWT vive en la cookie HttpOnly; se elimina tras migrar los hooks. */
	token?: string;
	/** epoch (segundos) de expiración del JWT, para el sessionTimer. */
	expiresAt?: number;
	isLogged: boolean;
	setUserData: (arg: User) => void;
	/** @deprecated CWE-922: usar setSession. Se mantiene para compat transitoria. */
	setUserToken: (arg: string) => void;
	setSession: (user: User, exp?: number) => void;
	/**
	 * Renueva la sesión sin re-loguear: pide un JWT nuevo a /renew (la cookie
	 * HttpOnly se re-emite server-side), actualiza `expiresAt` y re-arma el
	 * timer. Devuelve el nuevo epoch de expiración, o `undefined` si falló.
	 */
	renewSession: () => Promise<number | undefined>;
	userLogout: () => void;
};

export const useUserStore = create(
	persist<useUserStoreType>(
		(set) => ({
			user: undefined,
			token: undefined,
			expiresAt: undefined,
			isLogged: false,
			setUserData: (userData) => set(() => ({ user: userData })),
			setUserToken: (token) => set(() => ({ token, isLogged: true })),
			setSession: (user, exp) => {
				if (exp) startSessionTimer(exp);
				set(() => ({ user, expiresAt: exp, isLogged: true }));
			},
			renewSession: async () => {
				// POST autenticado por cookie; el interceptor agrega el CSRF.
				const { data } = await apiClient.post('/renew.json');
				const exp: number | undefined = data?.response?.exp;
				if (exp) {
					startSessionTimer(exp);
					set(() => ({ expiresAt: exp, isLogged: true }));
				}
				return exp;
			},
			userLogout: () => {
				clearSessionTimer();
				// CWE-922: cerrar sesión server-side (borra mc_token/mc_csrf).
				// Fire-and-forget; el POST lleva CSRF por el interceptor.
				apiClient.post('/logout.json').catch(() => {});
				set(() => ({
					user: undefined,
					token: undefined,
					expiresAt: undefined,
					isLogged: false,
				}));
			},
		}),
		{
			name: 'user-store',
			// CWE-922: NO persistir el token en localStorage. Solo perfil + exp.
			partialize: (state) =>
				({
					user: state.user,
					expiresAt: state.expiresAt,
					isLogged: state.isLogged,
				} as useUserStoreType),
			onRehydrateStorage: () => (state) => {
				if (state?.expiresAt) {
					startSessionTimer(state.expiresAt);
				}
			},
		}
	)
);
